/**
 * recentSessionScores — lightweight tracker for the last N session
 * accuracy percentages, used to detect *repeated* low scores so the
 * Reflection Card can soften its copy (§20.7 G6 decay-detection).
 *
 * Storage is intentionally separate from dataLayer to keep this slice
 * self-contained: a single localStorage key, last-3 ring buffer, no
 * cross-account magic. Repeated-struggle is defined as: 2+ of the
 * most recent 3 sessions scored < 50%.
 */

const KEY = 'mp_recent_session_scores'
const MAX = 3
const LOW_THRESHOLD = 50

function read(): number[] {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed)
      ? parsed.filter((n) => typeof n === 'number')
      : []
  } catch {
    return []
  }
}

function write(scores: number[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(scores.slice(-MAX)))
  } catch {
    // localStorage may be full or disabled — silent no-op
  }
}

/** Append the current session's percentage to the ring buffer. */
export function recordSessionScore(percentage: number) {
  if (
    typeof percentage !== 'number' ||
    Number.isNaN(percentage) ||
    percentage < 0 ||
    percentage > 100
  ) {
    return
  }
  const next = [...read(), percentage].slice(-MAX)
  write(next)
}

/** Read the recent scores including the current session. */
export function getRecentSessionScores(): number[] {
  return read()
}

/**
 * True when 2+ of the most recent 3 sessions scored below LOW_THRESHOLD.
 * Used to switch the Reflection Card to "soft, no-pressure" tone.
 */
export function hasRepeatedStruggle(): boolean {
  const recent = read()
  if (recent.length < 2) return false
  const lows = recent.filter((p) => p < LOW_THRESHOLD).length
  return lows >= 2
}
